import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  Loader2,
  Calendar,
  Users,
  Search,
  Database,
  Clock,
  FileText,
  BarChart2,
  Settings,
  MessageSquare,
  Zap,
  XCircle,
} from 'lucide-react';

export interface ActionItem {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  message: string;
  timestamp: Date;
}

interface RealTimeActionsProps {
  actions: ActionItem[];
  isVisible?: boolean;
}

const ACTION_ICONS: Record<string, any> = {
  get_appointments: Calendar,
  find_customer: Users,
  search_customers: Search,
  create_appointment: Calendar,
  update_appointment: Calendar,
  delete_appointment: XCircle,
  get_services: Database,
  get_staff: Users,
  get_available_time_slots: Clock,
  get_business_info: Settings,
  get_customer_stats: BarChart2,
  get_revenue: BarChart2,
  send_notification: MessageSquare,
  default: Zap,
};

const getIconForAction = (actionName: string) => {
  const lowerName = actionName.toLowerCase();
  for (const [key, Icon] of Object.entries(ACTION_ICONS)) {
    if (lowerName.includes(key.replace('_', ' ')) || lowerName.includes(key)) {
      return Icon;
    }
  }
  return ACTION_ICONS.default;
};

export const RealTimeActions = React.memo(({ actions, isVisible = true }: RealTimeActionsProps) => {
  if (!isVisible || actions.length === 0) {
    return null;
  }

  const hasActiveActions = actions.some(a => a.status === 'in_progress');
  const allCompleted = actions.every(a => a.status === 'completed');

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        layout: { duration: 0.3, ease: 'easeInOut' },
        opacity: { duration: 0.2 }
      }}
      className="border-b border-gray-100 overflow-hidden"
      style={{ willChange: 'transform' }}
    >
      <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 px-4 py-3">
        <div className="flex items-center gap-2 mb-3">
          {hasActiveActions ? (
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          ) : allCompleted ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <Clock className="h-4 w-4 text-gray-400" />
          )}
          <span className="text-xs font-bold text-gray-900">
            {hasActiveActions ? 'מבצע פעולות...' : allCompleted ? 'הושלם!' : 'פעולות'}
          </span>
          <span className="text-xs text-gray-500">
            {actions.filter(a => a.status === 'completed').length}/{actions.length}
          </span>
        </div>

        <div className="space-y-1.5 max-h-32 overflow-y-auto scroll-smooth">
          <AnimatePresence mode="sync">
            {actions.map((action, index) => {
              const Icon = getIconForAction(action.name);

              return (
                <motion.div
                  key={action.id}
                  layout
                  layoutId={action.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{
                    layout: { duration: 0.2, ease: 'easeOut' },
                    opacity: { duration: 0.15 },
                    x: { duration: 0.15 }
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    action.status === 'completed'
                      ? 'bg-green-50/80 border border-green-200/50'
                      : action.status === 'in_progress'
                      ? 'bg-blue-50/80 border border-blue-200/50 shadow-sm'
                      : action.status === 'error'
                      ? 'bg-red-50/80 border border-red-200/50'
                      : 'bg-white/60 border border-gray-200/50'
                  }`}
                >
                  <div className="flex-shrink-0 flex items-center justify-center">
                    {action.status === 'completed' ? (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{
                          type: 'spring',
                          stiffness: 500,
                          damping: 15,
                        }}
                      >
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      </motion.div>
                    ) : action.status === 'in_progress' ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    ) : action.status === 'error' ? (
                      <XCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="h-4 w-4 rounded-full border-2 border-gray-300"
                      />
                    )}
                  </div>

                  <Icon
                    className={`h-3.5 w-3.5 flex-shrink-0 ${
                      action.status === 'completed'
                        ? 'text-green-700'
                        : action.status === 'in_progress'
                        ? 'text-blue-700'
                        : action.status === 'error'
                        ? 'text-red-700'
                        : 'text-gray-400'
                    }`}
                  />

                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-xs font-medium truncate ${
                        action.status === 'completed'
                          ? 'text-green-900'
                          : action.status === 'in_progress'
                          ? 'text-blue-900'
                          : action.status === 'error'
                          ? 'text-red-900'
                          : 'text-gray-600'
                      }`}
                    >
                      {action.message}
                    </p>
                  </div>

                  {action.status === 'in_progress' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex-shrink-0"
                    >
                      <div className="flex gap-0.5">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            className="w-1 h-1 rounded-full bg-blue-500"
                            animate={{
                              scale: [1, 1.5, 1],
                              opacity: [0.5, 1, 0.5],
                            }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: i * 0.15,
                            }}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {allCompleted && actions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-2 pt-2 border-t border-green-200/50"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs text-green-700">
                <CheckCircle2 className="h-3 w-3" />
                <span className="font-semibold">כל הפעולות הושלמו</span>
              </div>
              <span className="text-xs text-green-600 font-mono">
                {actions.length} פעולות
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
});

RealTimeActions.displayName = 'RealTimeActions';
